import React, {forwardRef, useEffect, useRef, useState} from "react";
import {Autocomplete as MuiAutoComplete, CircularProgress, createFilterOptions, TextField} from "@mui/material";
import {Autocomplete as FormAutoComplete} from "mui-rff";
import {makeStyles} from "@mui/styles";
import Paper from "@mui/material/Paper";
import classnames from "classnames";
import Divider from "@mui/material/Divider";
import IconButton from "../button/icon_button";
import {Clear, Search} from "@mui/icons-material";
import useCombinedRefs from "../../../hooks/use-combined-refs";

const filter = createFilterOptions();

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '2px 15px',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        color: theme.vars.palette.background.paper,
        boxShadow: 'none',
        // boxShadow: props => props.shadow ? `0px 7px 7px ${theme.vars.palette['app-specific'].shadow}` : 'none',
    },
    input: {
        flex: 1,
        color: theme.vars.palette.background.paper,
        backgroundColor: props => props.backgroundColor,
    },
    innerInput: {
        borderRadius: 10,
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '&:focus': {
            backgroundColor: 'transparent',
        },
    },
    divider: {
        height: 25,
        margin: 4,
        width: 1,
        color: theme.vars.palette.divider,
    },
    paper: {
        height: '100%',
        minHeight: '70px',
    },
    autocompletePaper: {
        marginTop: '1.8rem',
    },
}))

const oldReason = (reason) => {
    switch (reason) {
        case 'selectOption':
            return 'select-option';
        case 'createOption':
            return 'create-option';
        case 'removeOption':
            return 'remove-option';
        case 'clear':
            return 'clear';
        default:
            return '';
    }
};

export const AutoComplete = ({
                                 options,
                                 placeholder = 'type...',
                                 label,
                                 ariaLabel = 'input',
                                 onSelect,
                                 onRemove,
                                 onChange,
                                 fullWidth = true,
                                 multiple,
                                 variant = 'standard',
                                 disableCloseOnSelect,
                                 selectOnFocus,
                                 clearOnBlue,
                                 handleHomeEndKeys,
                                 freeSolo = true,
                                 loading,
                                 loadingText = 'loading...',
                                 filterOptions,
                                 addNewText = (input) => `Add ${input}`,
                                 renderOption,
                                 inputValue,
                                 value,
                                 optionSelected,
                                 onSelectInner: onSelectInnerProps,
                                 ...props
                             }) => {
    const classes = useStyles({shadow: true});
    const [open, setOpen] = useState(false);


    const defaultFilterOptions = (options, params) => {
        const filtered = filter(options, params);
        if (freeSolo) {
            if (params.inputValue !== '') {
                filtered.push({
                    value: {title: params.inputValue, id: -1},
                    title: addNewText(params.inputValue),
                    id: -1,
                });
            }
        }
        return filtered;
    }

    const getOptionLabel = (option) => {
        if (typeof option === 'string') return option;
        // Add "xxx" option created dynamically
        if (option.id === -1) return option.value.title;
        // Regular option
        return option.title;
    }

    const defaultRenderOption = (props, option, state) => {
        const content = renderOption?.(props, option, state) ?? function () {
            if (!option) return '';
            if (typeof option === 'string') return (
                <p className={classnames('mx-2 p-2', props.className)}>{option}</p>
            );
            // Add "xxx" option created dynamically
            if (option.id === -1) return (
                <p className='mx-2 p-2'>
                    {option.value.title ?? option.value.label}
                </p>
            )
            // Regular option
            return <p className='mx-2 p-2'>
                {option.title ?? option.label}
            </p>
        }();
        return (
            <li {...props} key={props.id}>
                {content}
            </li>
        );
    }


    const defaultOptionSelected = (option, value) => option.name === value.name;

    const onSelectInner = (e, value, _reason) => {
        const reason = oldReason(_reason);
        e.stopPropagation();
        e.preventDefault();
        if (onSelectInnerProps) {
            return onSelectInnerProps(e, value, reason);
        }
        if (reason === 'select-option') {
            if (onSelect) onSelect(e, value);
        }
        if (reason === 'clear') {
            if (onRemove) onRemove()
        }
    }

    return (
        <Paper className={classnames(classes.root, classes.paper)}>
            <MuiAutoComplete
                inputValue={inputValue}
                value={value}
                open={open}
                onChange={onSelectInner}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                isOptionEqualToValue={optionSelected ?? defaultOptionSelected}
                getOptionLabel={getOptionLabel}
                options={options}
                loading={loading}
                loadingText={loadingText}
                aria-label={ariaLabel}
                aria-multiline={multiple}
                freeSolo={freeSolo}
                multiple={multiple}
                filterOptions={filterOptions ?? defaultFilterOptions}
                selectOnFocus={selectOnFocus}
                clearOnBlur={clearOnBlue}
                handleHomeEndKeys={handleHomeEndKeys}
                fullWidth={fullWidth}
                disableCloseOnSelect={disableCloseOnSelect}
                renderOption={defaultRenderOption}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant={variant}
                        placeholder={placeholder}
                        aria-label={ariaLabel}
                        label={label}
                        onChange={onChange}
                        classes={{
                            root: "autocomplete-with-paper",
                        }}
                        InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            endAdornment: (
                                <>
                                    {loading && <CircularProgress color="primary" size={20}/>}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                {...props}
                classes={{
                    ...(props.classes ?? {}),
                    paper: classnames(props?.classes?.paper, classes.autocompletePaper),
                }}
            />
        </Paper>
    );
}


/**
 * @param {formApi} form
 * @param {boolean} clearOnSelect
 * @param {boolean} doNotSubmitFormChanges
 * @param textFieldProps
 * @param {React.ReactNode} endAdornment
 * @param {boolean} fullWidth
 * @param placeholder
 * @param {import("@mui/material").AutocompleteProps & import("mui-rff").AutocompleteProps} props
 * @param ref
 * @return {JSX.Element}
 */
export const AutoCompleteForm = forwardRef(({
                                                clearOnSelect = false,
                                                doNotSubmitFormChanges = false,
                                                form = undefined,
                                                textFieldProps,
                                                endAdornment,
                                                fullWidth = true,
                                                placeholder = '',
                                                renderOption,
                                                ...props
                                            }, ref) => {
    const classes = useStyles({shadow: true});
    const [value, setValue] = useState(props.value ?? null);
    const innerRef = useRef();
    const combinedRef = useCombinedRefs(innerRef, ref);
    const [open, setOpen] = useState(false);

    /**
     * With each change in the value:
     * - if clearOnSelect, then removes the state value (if Form, uses the form api for it)
     */
    useEffect(() => {
        if (value && clearOnSelect) {
            setValue(null);
            form?.resetFieldState(props.name);
        }
    }, [value])

    /**
     * Changes the value of the autocomplete (form or non-form) and then calls the onChange property of the props if
     * it exists.
     * @param {React.ChangeEvent} e
     * @param {any} value
     * @param {string} _reason
     */
    const onChanged = (e, value, _reason) => {
        const reason = oldReason(_reason);
        if (props.onChange) {
            props.onChange(e, value, reason);
        }
        if (!doNotSubmitFormChanges)
            form?.change(props.name, value);
        setValue(value);
    }

    const defaultRenderOption = (props, option, state) => {
        const content = renderOption?.(props, option, state);
        return (
            <li {...props} key={props.id}>
                {content}
            </li>
        );
    }

    const onButtonClicked = () => {
        const input = innerRef.current?.querySelector('input');
        if (value || !!input?.value) {
            onChanged(null, null, 'clear');
            if (input) {
                input.value = '';
            }
        } else {
            setOpen(true);
        }
    }

    return (
        <>
            <Paper className={classnames(classes?.root, classes?.paper)}>
                <FormAutoComplete
                    placeholder={placeholder}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    open={open}
                    {...props}
                    {...(renderOption && {renderOption: defaultRenderOption})}
                    forcePopupIcon={false}
                    classes={{
                        paper: classnames(classes?.autocompletePaper, textFieldProps?.classes?.paper),
                    }}
                    textFieldProps={{
                        ...textFieldProps,
                        ref: combinedRef,
                        variant: "standard",
                        className: classnames(textFieldProps?.className, classes?.input),
                        classes: {
                            ...(textFieldProps?.classes ?? {}),
                            root: classnames(classes?.innerInput, textFieldProps?.classes?.root),
                            paper: undefined,
                        },
                        InputProps: {
                            disableUnderline: true,
                            endAdornment: (
                                <>
                                    <Divider className={classes.divider} orientation="vertical"/>
                                    <IconButton
                                        onPressed={onButtonClicked}
                                        color={'paper.secondary'}
                                    >
                                        {
                                            (!!form.getState().values?.[props?.name])
                                                ? <Clear/>
                                                : <Search/>
                                        }
                                    </IconButton>
                                </>
                            ),
                            ...(textFieldProps?.InputProps ?? {}),
                        },
                    }}
                    fullWidth={fullWidth}
                    onChange={onChanged}
                />
            </Paper>
        </>
    )
});

